<template>
  <!-- Kit ClAlert (the app's cl_alert.dart, the Alerts board): a 270 px
       card over the scrim, title 17/600, body 13, an optional field, then
       44 px actions split by hairlines, side by side or stacked (the last
       on top) when there are more than two or a label, measured in its own
       font, won't fit half the row (kit/alert.js). Plain and bold actions
       are accent text (bold 600); destructive is red. -->
  <div class="cl-alert-scrim" @click.self="$emit('dismiss')" @keydown.esc="$emit('dismiss')">
    <div class="cl-alert" role="alertdialog" :aria-label="title" aria-modal="true">
      <div class="cl-alert-content">
        <div class="cl-alert-title">{{ title }}</div>
        <div v-if="body" class="cl-alert-body">{{ body }}</div>
        <input
          v-if="field"
          ref="field"
          v-model="text"
          class="cl-alert-field"
          type="text"
          :placeholder="field.placeholder || ''"
          :maxlength="field.maxLength || 100"
          @keydown.enter.prevent="chooseDefault"
        />
      </div>
      <div class="cl-alert-actions" :class="{ 'is-stacked': stacked }">
        <button
          v-for="a in ordered"
          :key="a.i"
          type="button"
          class="cl-alert-action"
          :class="`is-${a.action.kind || 'plain'}`"
          @click="choose(a.action)"
        >
          {{ a.action.label }}
        </button>
      </div>
      <!-- Each label in its action's font, for the stacking rule. -->
      <div class="cl-alert-measure" aria-hidden="true">
        <span v-for="(a, i) in actions" :key="i" ref="measure" class="cl-alert-measure-label" :class="`is-${a.kind || 'plain'}`">{{ a.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { alertActionOrder, stackAlertActions } from "@/kit/alert.js";

export default {
  name: "ClAlert",
  props: {
    title: { type: String, required: true },
    body: { type: String, default: "" },
    // [{ label, kind: "plain" | "bold" | "destructive", value }]
    actions: { type: Array, default: () => [] },
    // { value, placeholder, maxLength }
    field: { type: Object, default: null },
  },
  emits: ["choose", "dismiss"],
  data() {
    return { text: this.field ? String(this.field.value || "") : "", widths: null };
  },
  computed: {
    stacked() {
      return stackAlertActions(this.actions, this.widths);
    },
    ordered() {
      const indexed = this.actions.map((action, i) => ({ action, i }));
      return alertActionOrder(indexed, this.stacked);
    },
  },
  watch: {
    actions() {
      this.widths = null;
      this.$nextTick(this.measure);
    },
  },
  mounted() {
    // Measured before the first paint (the update lands in the same
    // frame); again once a web font has loaded.
    this.measure();
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.measure()).catch(() => {});
    }
    this.$nextTick(() => {
      if (this.$refs.field) {
        this.$refs.field.focus();
        this.$refs.field.select();
      }
    });
  },
  methods: {
    measure() {
      const spans = this.$refs.measure;
      if (!Array.isArray(spans) || spans.length !== this.actions.length) return;
      this.widths = spans.map((el) => (el && el.getBoundingClientRect ? el.getBoundingClientRect().width : 0));
    },
    choose(a) {
      this.$emit("choose", a.value === "@field" ? this.text.trim() : a.value, this.text);
    },
    chooseDefault() {
      const a = [...this.actions].reverse().find((x) => x.kind !== "plain") || this.actions[this.actions.length - 1];
      if (a) this.choose(a);
    },
  },
};
</script>

<style scoped>
.cl-alert-scrim {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cl-scrim);
}
.cl-alert {
  position: relative;
  width: 270px;
  max-width: calc(100vw - 32px);
  border-radius: var(--cl-r-2xl);
  background: var(--cl-alert);
  box-shadow: 0 16px 40px var(--cl-scrim);
  overflow: hidden;
  color: var(--cl-label);
  font-family: var(--cl-font);
}
.cl-alert-content {
  padding: 19px 16px 16px;
  text-align: center;
}
.cl-alert-title {
  font: 600 17px/22px var(--cl-font);
  color: var(--cl-label);
}
.cl-alert-body {
  margin-top: 4px;
  font: 400 13px/18px var(--cl-font);
  color: var(--cl-label-78);
  white-space: pre-line;
}
.cl-alert-field {
  width: 100%;
  margin-top: 14px;
  height: 28px;
  padding: 0 8px;
  border-radius: 7px;
  border: 1px solid var(--cl-border-default);
  background: var(--cl-bg);
  color: var(--cl-label);
  font: 400 14px/18px var(--cl-font);
  outline: none;
  box-sizing: border-box;
}
.cl-alert-field:focus {
  border-color: var(--cl-accent);
}
.cl-alert-actions {
  display: flex;
  border-top: 1px solid var(--cl-border-default);
}
.cl-alert-actions.is-stacked {
  flex-direction: column;
}
.cl-alert-action,
.cl-alert-measure-label {
  font: 400 17px/22px var(--cl-font);
  white-space: nowrap;
}
.cl-alert-action {
  flex: 1 1 0;
  min-width: 0;
  height: 44px;
  padding: 0 8px;
  border: none;
  background: transparent;
  color: var(--cl-accent-text);
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}
/* Stacked, each action keeps its 44 (a 0 basis would collapse it to its
   line in a column). */
.is-stacked .cl-alert-action {
  flex: 0 0 auto;
}
.cl-alert-action + .cl-alert-action {
  border-left: 1px solid var(--cl-border-default);
}
.is-stacked .cl-alert-action + .cl-alert-action {
  border-left: none;
  border-top: 1px solid var(--cl-border-default);
}
.cl-alert-action.is-bold,
.cl-alert-measure-label.is-bold {
  font-weight: 600;
}
.cl-alert-measure {
  position: absolute;
  left: 0;
  top: 0;
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.cl-alert-action.is-destructive {
  color: var(--cl-destructive);
}
@media (hover: hover) {
  .cl-alert-action:hover {
    background: var(--cl-hover);
  }
}
</style>
